import type { ReactNode } from "react";
import Reveal from "@/components/base/Reveal";

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: string;
  image: string;
  imageClassName?: string;
  align?: "left" | "center";
  meta?: ReactNode;
}

export default function PageHero({
  eyebrow,
  title,
  subtitle,
  image,
  imageClassName = "object-center",
  align = "left",
  meta,
}: PageHeroProps) {
  const centered = align === "center";

  return (
    <section className="relative flex min-h-[78vh] md:min-h-[88vh] items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className={`h-full w-full object-cover ${imageClassName} animate-kenburns`}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/30" />
      <div className="absolute inset-0 bg-foreground-950/20" />

      <div
        className={`relative z-10 w-full px-5 md:px-10 lg:px-16 pb-16 md:pb-24 ${
          centered ? "text-center" : ""
        }`}
      >
        <div className={centered ? "mx-auto max-w-4xl" : "max-w-4xl"}>
          <Reveal>
            <p className="eyebrow text-primary-400">{eyebrow}</p>
          </Reveal>
          <Reveal delay={120}>
            <h1
              className={`mt-5 font-heading text-4xl leading-[1.02] text-background-50 sm:text-5xl md:text-6xl lg:text-7xl ${
                centered ? "" : ""
              }`}
            >
              {title}
            </h1>
          </Reveal>
          {subtitle && (
            <Reveal delay={220}>
              <p
                className={`mt-6 max-w-2xl font-body text-base leading-relaxed text-background-50/75 md:text-lg ${
                  centered ? "mx-auto" : ""
                }`}
              >
                {subtitle}
              </p>
            </Reveal>
          )}
          {meta && (
            <Reveal delay={320}>
              <div className="mt-10">{meta}</div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}